'use client'

import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import type { Variants } from 'framer-motion'
import {
    FaBrain,
    FaRegCompass,
    FaBolt,
    FaClock,
    FaUserTie,
    FaChevronRight,
    FaChevronDown,
    FaBullseye,
    FaLayerGroup,
    FaChartLine,
    FaHandshake
} from 'react-icons/fa'
import { cx } from '@/lib/utils'

type ProgramKey = 'module' | 'seminar' | 'followup'
type FlowStep = { label: string }

type Program = {
    key: ProgramKey
    title: string
    subtitle: string
    icon: React.ReactNode

    duration: string
    audience: string
    promise: string

    flow: FlowStep[]
    deliverables: string[]
    outcomes: { title: string; desc: string; icon: React.ReactNode }[]
    note?: string
}

const programs: Program[] = [
    {
        key: 'seminar',
        title: 'Introductory Seminar',
        subtitle: '3 hours · Live or online',
        icon: <FaRegCompass aria-hidden='true' className='h-5 w-5' />,
        duration: '3 hours',
        audience: 'Teams & leadership groups',
        promise: 'A shared framework for stress + strength-led performance.',
        flow: [
            { label: 'Discover – What creates trust' },
            { label: 'Share – Understand each other' },
            { label: 'Define – What does trust mean for our team' },
            { label: 'Agree – Create a trust code' },
            { label: 'Commit – Turn trust into action' }
        ],
        deliverables: ['Trust Indicator', 'Team Trust Code', 'Trust Action Plan'],
        outcomes: [
            {
                title: 'Trust',
                desc: 'Team develops greater trust and openness. Better communication.',
                icon: <FaHandshake aria-hidden='true' />
            },
            {
                title: 'Practical tools',
                desc: 'Members learn self management.',
                icon: <FaBullseye aria-hidden='true' />
            },
            {
                title: 'Collaboration',
                desc: 'Better cross functional collaboration.',
                icon: <FaLayerGroup aria-hidden='true' />
            }
        ],
        note: 'Delivered live or online depending on client wishes.'
    },
    {
        key: 'followup',
        title: 'Individual Executive Coaching',
        subtitle: '6 × 1 hour · Key personnel',
        icon: <FaBolt aria-hidden='true' className='h-5 w-5' />,
        duration: '6 sessions',
        audience: 'Key leaders / high-impact roles',
        promise: 'Turn insight into consistent execution.',
        flow: [
            { label: 'Awareness' },
            { label: 'Map' },
            { label: 'Reframe' },
            { label: 'Regulate' },
            { label: 'Execute' }
        ],
        deliverables: [
            'Leadership Assessment',
            'Individual Leadership Action Plan',
            'Progress and Accountability Review'
        ],
        outcomes: [
            { title: 'Consistency', desc: 'More reliable follow-through.', icon: <FaChartLine aria-hidden='true' /> },
            { title: 'Better feedback', desc: 'Noticeable 360° shifts.', icon: <FaBullseye aria-hidden='true' /> },
            { title: 'Stronger leadership', desc: 'Calm, clear decisions.', icon: <FaLayerGroup aria-hidden='true' /> }
        ],
        note: 'Measurable change is often visible within ~3 months.'
    },
    {
        key: 'module',
        title: 'Neurodiverse Coaching Module',
        subtitle: '6 × 60–90 min · Executive coaching',
        icon: <FaBrain aria-hidden='true' className='h-5 w-5' />,
        duration: '6 sessions',
        audience: 'Leaders & key talent',
        promise: 'Clarity → alignment → execution under pressure.',
        flow: [
            { label: 'Personal Audit Strength identifiers' },
            { label: 'Trigger analysis' },
            { label: 'Regulate' },
            { label: 'Working Architecture' },
            { label: 'Implementation' }
        ],
        deliverables: ['Improved collaboration', 'Higher Productivity', 'Reduced Absenteeism & burnout'],
        outcomes: [
            {
                title: 'Strength alignment',
                desc: 'Lead with what works—repeatably.',
                icon: <FaBullseye aria-hidden='true' />
            },
            {
                title: 'Manage Challenges',
                desc: 'Less stress-driven friction.',
                icon: <FaChartLine aria-hidden='true' />
            },
            {
                title: 'Workload balance',
                desc: 'Clear priorities & boundaries.',
                icon: <FaLayerGroup aria-hidden='true' />
            }
        ]
    }
]

const EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1]

const panelIn: Variants = {
    hidden: { opacity: 0, y: 12, filter: 'blur(8px)' },
    show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.55, ease: EASE } }
}

function Hairline() {
    return (
        <div className='h-px w-full bg-[linear-gradient(to_right,transparent,color-mix(in_oklab,var(--color-border)_70%,transparent),transparent)]' />
    )
}

function MetaPill({ icon, label }: { icon: React.ReactNode; label: string }) {
    return (
        <span className='inline-flex items-center gap-2 text-sm text-muted'>
            <span className='text-text/70'>{icon}</span>
            <span className='font-semibold text-text/90'>{label}</span>
        </span>
    )
}

function Disclosure({
    title,
    subtitle,
    open,
    onToggle,
    children
}: {
    title: string
    subtitle?: string
    open: boolean
    onToggle: () => void
    children: React.ReactNode
}) {
    return (
        <div className='mt-8'>
            <button
                type='button'
                onClick={onToggle}
                className={cx(
                    'group w-full text-left',
                    'flex items-center justify-between gap-6',
                    'rounded-2xl px-4 py-4',
                    'bg-[color-mix(in_oklab,var(--color-soft)_85%,transparent)]',
                    'transition-[transform,opacity] duration-200',
                    'hover:-translate-y-px',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_oklab,var(--color-accent)_45%,transparent)]'
                )}
                aria-expanded={open}>
                <div>
                    <p className='text-sm font-semibold tracking-tight'>{title}</p>
                    {subtitle ? <p className='mt-1 text-sm text-muted'>{subtitle}</p> : null}
                </div>
                <span className='inline-flex items-center gap-2 text-sm font-semibold text-muted'>
                    {open ? 'Hide' : 'Show'}
                    <FaChevronDown
                        className={cx('h-4 w-4 transition-transform duration-200', open ? 'rotate-180' : 'rotate-0')}
                    />
                </span>
            </button>

            <motion.div
                initial={false}
                animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
                transition={{ duration: 0.28, ease: EASE }}
                className='overflow-hidden'>
                <div className='px-4 pt-5'>{children}</div>
            </motion.div>
        </div>
    )
}

function ProgramNav({ active, onChange }: { active: ProgramKey; onChange: (k: ProgramKey) => void }) {
    return (
        <div className='lg:sticky lg:top-24'>
            <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>SELECT FORMAT</p>
            <p className='mt-2 text-sm text-muted'>Choose one to view details.</p>

            <div role='tablist' aria-label='Programs' className='mt-6 space-y-2'>
                {programs.map((p) => {
                    const selected = p.key === active
                    return (
                        <button
                            key={p.key}
                            role='tab'
                            aria-selected={selected}
                            type='button'
                            onClick={() => onChange(p.key)}
                            className={cx(
                                'group relative w-full text-left rounded-2xl px-4 py-4',
                                'transition-[transform,opacity] duration-200',
                                'hover:-translate-y-px',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_oklab,var(--color-accent)_45%,transparent)]',
                                selected ? 'opacity-100' : 'opacity-75 hover:opacity-100'
                            )}>
                            {/* Active marker: simple vertical bar + soft wash (no borders) */}
                            {selected ? (
                                <>
                                    <motion.span
                                        layoutId='nav-wash'
                                        className='absolute inset-0 rounded-2xl bg-[color-mix(in_oklab,var(--color-accent)_12%,transparent)]'
                                        transition={{ duration: 0.25, ease: EASE }}
                                        aria-hidden='true'
                                    />
                                    <motion.span
                                        layoutId='nav-bar'
                                        className='absolute left-0 top-3 bottom-3 w-1 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_55%,transparent)]'
                                        transition={{ duration: 0.25, ease: EASE }}
                                        aria-hidden='true'
                                    />
                                </>
                            ) : null}

                            <div className='relative flex items-start gap-4'>
                                <span
                                    className={cx(
                                        'mt-0.5 grid h-11 w-11 place-items-center rounded-2xl',
                                        selected
                                            ? 'bg-[color-mix(in_oklab,var(--color-accent)_18%,transparent)]'
                                            : 'bg-[color-mix(in_oklab,var(--color-soft)_88%,transparent)]'
                                    )}>
                                    {p.icon}
                                </span>

                                <div className='min-w-0 flex-1'>
                                    <p className='text-base font-semibold tracking-tight'>{p.title}</p>
                                    <p className='mt-1 text-sm text-muted'>{p.subtitle}</p>
                                </div>

                                <span
                                    className={cx(
                                        'mt-1 inline-flex items-center gap-2 text-sm font-semibold',
                                        selected ? 'text-text/75' : 'text-muted'
                                    )}>
                                    View
                                    <FaChevronRight className='h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5' />
                                </span>
                            </div>
                        </button>
                    )
                })}
            </div>

            <div className='mt-8'>
                <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>RATIONALE</p>
                <p className='mt-3 text-sm text-muted leading-relaxed'>
                    Retention, performance sustainability, leadership pipeline—delivered through practical
                    self-regulation and strength alignment.
                </p>
            </div>
        </div>
    )
}

function FlowInline({ flow }: { flow: FlowStep[] }) {
    return (
        <div className='grid gap-3'>
            {flow.map((s, i) => (
                <div key={s.label} className='flex items-start gap-3'>
                    <span className='mt-1 text-xs font-semibold text-muted w-8'>{String(i + 1).padStart(2, '0')}</span>
                    <span className='text-sm font-semibold text-text/90'>{s.label}</span>
                </div>
            ))}
        </div>
    )
}

function DeliverablesInline({ items }: { items: string[] }) {
    return (
        <div className='grid gap-3 sm:grid-cols-2'>
            {items.map((t) => (
                <div key={t} className='flex gap-3'>
                    <span className='mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[color-mix(in_oklab,var(--color-accent-2)_60%,transparent)]' />
                    <p className='text-sm text-text/90 leading-relaxed'>{t}</p>
                </div>
            ))}
        </div>
    )
}

function OutcomesPrimary({ outcomes }: { outcomes: Program['outcomes'] }) {
    return (
        <div className='mt-8'>
            <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>KEY OUTCOMES</p>
            <div className='mt-5 grid gap-6 sm:grid-cols-3'>
                {outcomes.map((o) => (
                    <div key={o.title} className='flex gap-3'>
                        <span className='mt-0.5 grid h-10 w-10 place-items-center rounded-2xl bg-[color-mix(in_oklab,var(--color-soft)_88%,transparent)] text-text'>
                            {o.icon}
                        </span>
                        <div className='min-w-0'>
                            <p className='text-sm font-semibold tracking-tight'>{o.title}</p>
                            <p className='mt-1 text-sm text-muted leading-relaxed'>{o.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function ProgramsSection() {
    const [active, setActive] = useState<ProgramKey>('seminar')
    const current = useMemo(() => programs.find((p) => p.key === active) ?? programs[0], [active])

    // Progressive disclosure toggles
    const [openFlow, setOpenFlow] = useState(true)
    const [openDeliverables, setOpenDeliverables] = useState(false)

    // when changing program, reset disclosure to a calm default
    // (adjust state during render instead of in an effect to avoid cascading renders)
    const [prevActive, setPrevActive] = useState(active)
    if (active !== prevActive) {
        setPrevActive(active)
        setOpenFlow(true)
        setOpenDeliverables(false)
    }

    return (
        <section id='programs' className='relative overflow-hidden bg-page text-text'>
            {/* cohesive background wash */}
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <div className='absolute -left-56 -top-48 h-180 w-180 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_10%,transparent)] blur-3xl' />
                <div className='absolute -right-72 -bottom-64 h-215 w-215 rounded-full bg-[color-mix(in_oklab,var(--color-accent-2)_9%,transparent)] blur-3xl' />
                <div className='absolute inset-0 bg-[radial-gradient(1200px_700px_at_20%_0%,color-mix(in_oklab,var(--color-soft)_55%,transparent),transparent_62%)]' />
            </div>

            <div className='container-x section-pad relative'>
                <div className='max-w-205'>
                    <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>PROGRAMS</p>
                    <h2 className='mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl'>
                        Three formats.
                        <span className='block'>One outcome: execution you can trust.</span>
                    </h2>
                    <p className='mt-4 text-pretty text-muted sm:text-lg'>
                        Select a format. Details are deliberately structured—so decision-makers don’t drown in
                        information.
                    </p>
                </div>

                {/* One shared surface to avoid “two random blocks” */}
                <div className='mt-12 surface-strong p-6 sm:p-8'>
                    <div className='grid gap-10 lg:grid-cols-[380px_1fr] lg:items-start'>
                        <ProgramNav active={active} onChange={setActive} />

                        <motion.div
                            key={current.key}
                            role='tabpanel'
                            aria-label={`${current.title} details`}
                            variants={panelIn}
                            initial='hidden'
                            animate='show'
                            className='min-w-0'>
                            {/* HERO */}
                            <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>OVERVIEW</p>
                            <h3 className='mt-4 text-balance text-3xl font-semibold tracking-tight sm:text-4xl'>
                                {current.title}
                            </h3>
                            <p className='mt-3 text-pretty text-muted sm:text-lg'>{current.promise}</p>

                            <div className='mt-6 flex flex-wrap items-center gap-x-6 gap-y-2'>
                                <MetaPill icon={<FaClock className='h-4 w-4' />} label={current.duration} />
                                <span
                                    className='h-4 w-px bg-[color-mix(in_oklab,var(--color-border)_55%,transparent)]'
                                    aria-hidden='true'
                                />
                                <MetaPill icon={<FaUserTie className='h-4 w-4' />} label={current.audience} />
                            </div>

                            <div className='mt-10'>
                                <Hairline />
                            </div>

                            {/* Make the buying argument immediate */}
                            <OutcomesPrimary outcomes={current.outcomes} />

                            {/* Everything else is on demand */}
                            <Disclosure
                                title='Flow'
                                subtitle='The sequence we follow (simple, repeatable).'
                                open={openFlow}
                                onToggle={() => setOpenFlow((v) => !v)}>
                                <FlowInline flow={current.flow} />
                            </Disclosure>

                            <Disclosure
                                title='Deliverables'
                                subtitle='What you receive (artifacts + plans).'
                                open={openDeliverables}
                                onToggle={() => setOpenDeliverables((v) => !v)}>
                                <DeliverablesInline items={current.deliverables} />
                            </Disclosure>

                            {current.note ? (
                                <p className='mt-8 text-sm text-muted leading-relaxed'>{current.note}</p>
                            ) : null}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    )
}
